import { CustomerModel, useSuspendCustomer } from 'api-hooks/customer';
import { AuthorizationRules } from 'common/constants';
import { UserStatusEnum } from 'common/constants/enum';
import notification from 'components/common/notification';
import { PopoverMenuType } from 'components/popover';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useCustomerUnsuspendAction(customer?: CustomerModel) {
  const { can } = useAuthorization();
  const { t } = useTranslation();
  const dialog = useDialog();
  const suspendCustomerMutate = useSuspendCustomer();
  const onDismiss = useDismiss('customers');
  const id = customer?.id;

  const onCustomerUnsuspend = React.useCallback(
    async (currentId?: string) => {
      const _id = currentId ?? id;

      const message = {
        id: _id || 'undefined',
        message: 'Invalid Customer Unsuspend Action (FE)',
      };

      if (!_id) {
        notification.error({
          message: JSON.stringify(message),
        });
        return;
      }

      dialog.showConfirmation({
        message: t('common:confirmation_unsuspend_text'),
        title: t('common:confirmation'),
        onPositiveAction: async (onClose) => {
          try {
            await suspendCustomerMutate.mutateAsync({
              customerId: _id,
              body: { action: UserStatusEnum.Safe },
            });

            notification.success({
              message: t('common:successfully_unsuspended'),
            });
            onClose();
            onDismiss();
          } catch (error) {
            notification.error({ message: error.message });
          }
        },
        onNegativeAction: (onClose) => {
          onClose();
        },
      });
    },
    [dialog, id, onDismiss, suspendCustomerMutate, t],
  );

  const customerUnsuspendPermission =
    can(AuthorizationRules.CustomersUpdateSurveillance) &&
    customer?.status === UserStatusEnum.UnderSurveillance;

  const customerUnsuspendAction: PopoverMenuType = {
    key: `customer-unsuspend-${customer?.id}`,
    label: t('customer:unsuspend'),
    onClick: onCustomerUnsuspend,
    isAllowed: customerUnsuspendPermission,
    isError: true,
  };

  return {
    onCustomerUnsuspend,
    customerUnsuspendAction,
    customerUnsuspendPermission,
  };
}
